import { getTradingOrderDetailsRoute } from '@src/containers/Routes/routes';
import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { useContextBlotterFilter } from '@src/providers/ContextBlotterFilterProvider/useContextBlotterFilter';
import {
  BlotterDensity,
  BlotterTable,
  BlotterTableFilters,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExportDialog,
  MaxRecordsReachedWarning,
  MixpanelEvent,
  SummaryLine,
  createExportFileName,
  filterByColumnMainMenuItems,
  getOrderAmount,
  getOrderQuantity,
  isGridApiReady,
  orderStatusFilterTextToBackendStatus,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTable,
  useDateRangeFilter,
  useDisclosure,
  useMixpanel,
  useObservable,
  usePersistedBlotterTable,
  type BlotterTableFilter,
  type Column,
  type CompositePipeFunction,
  type ExportGridMode,
  type FilterClause,
  type Order,
  type PricingModeEnum,
  type RequestSourceEnum,
  type TimestampField,
  type UnifiedLiquidityEnum,
} from '@talos/kyoko';
import type {
  GetMainMenuItemsParams,
  RowClassParams,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-enterprise';
import { isEqual, pick } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { map, pipe } from 'rxjs';
import { combineLatestWith } from 'rxjs/operators';
import { useDetailsDrawer } from '../../../components/DetailsDrawer/useDetailsDrawer';
import { useEmbeddedContext } from '../../../hooks';
import { useNavigateToTradingOrderDetails } from '../../../hooks/useNavigateToTradingOrderDetails';
import { useOrderColumns } from './Columns';
import { OrderOptions } from './OrderOptions';
import { useFilteredOrdersBlotterObs } from './useFilteredOrdersBlotterObs';
import { useHandleExportOrders } from './useHandleExportOrders';
import {
  colIDToFilterBuilderKey,
  useOrderFilter,
  type BlotterTableOrderFilter,
  type UseOrderFilterOutput,
} from './useOrderFilter';
import { useOrderMenu } from './useOrderMenu';
import { useRecentCustomerOrders } from './useRecentCustomerOrders';

interface FilteredOrdersParams {
  blotterID: string;
  tabLabel?: string;
  defaultFilter: BlotterTableOrderFilter;
  /* filters that are always applied, not visible in the UI and cannot be changed */
  permanentFilters?: BlotterTableFilter;
  defaultColumns?: (keyof Order | Partial<Column>)[];

  onCloneTab?: () => void;

  /** When mounting should the filter accordian be expanded */
  initialIsOpen?: boolean;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<Order>) => void;
  generateOrderDetailsRoute?: GenerateOrderDetailsRoute;

  /** Skip showing Customer columns even when enableCustomerBlotterColumns toggle is on */
  suppressCustomerColumns?: boolean;
  /** Always show DDH orders and do not show Toggle to the user */
  alwaysShowDDHOrders?: boolean;

  persistFilter?: boolean;
  /** Layout ID, currently only used for opening order details in the trading layout */
  layoutId: string | undefined;
}

type OrderTimestampField = keyof Pick<Order, 'SubmitTime' | 'Timestamp' | 'LastTradeTime'> | '';

const timestampFields: TimestampField<OrderTimestampField>[] = [
  {
    Name: '',
    DisplayName: 'Default',
    Description: 'Returns any orders submitted or updated in the specified period.',
    default: true,
  },
  {
    Name: 'SubmitTime',
    DisplayName: 'Submit Date',
    Description: 'Filters by order submit timestamp.',
    default: false,
  },
  {
    Name: 'Timestamp',
    DisplayName: 'Updated At',
    Description: 'Filters by order update timestamp.',
    default: false,
  },
  {
    Name: 'LastTradeTime',
    DisplayName: 'Last Trade Time',
    Description: 'Filters by the latest trade execution timestamp.',
    default: false,
  },
];

export function FilteredOrders({
  blotterID,
  tabLabel,
  defaultFilter,
  permanentFilters,
  defaultColumns,
  onCloneTab,
  initialIsOpen,
  onRowDoubleClicked,
  generateOrderDetailsRoute = getTradingOrderDetailsRoute,
  suppressCustomerColumns,
  alwaysShowDDHOrders,
  persistFilter,
  layoutId,
}: FilteredOrdersParams) {
  const mixpanel = useMixpanel();
  const { homeCurrency } = useDisplaySettings();
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);
  const { isEmbedded } = useEmbeddedContext();
  const { openDetailsDrawer } = useDetailsDrawer();

  const { filterPropsMutator, additionalFilterState } = useContextBlotterFilter<UseOrderFilterOutput>();

  const columns = useOrderColumns({ defaultColumns, suppressCustomerColumns });
  const persisted = usePersistedBlotterTable(blotterID, { filter: defaultFilter }, { persistFilter });

  const filteredOrders = useOrderFilter({
    initialFilter: persisted.initialState?.filter,
    permanentFilters,
    saveFilter: persisted.onFilterChanged,
    includeStatusFilter: true,
    includeIDFilter: true,
    additionalFilterState,
    alwaysShowDDHOrders,
  });

  const { clientSideFilter, changeFilter, filterableProperties, initialFilterClauses } = useMemo(() => {
    return filterPropsMutator(filteredOrders);
  }, [filterPropsMutator, filteredOrders]);

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<Order>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const selectedRows = api.getSelectedRows();
    setSelectedOrders(selectedRows);
  }, []);

  // Forward the parts of the filter being sent to the backend
  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters({
          ...curr,
          Markets: filterClausesByPropertyKey.get('Markets')?.selections,
          SelectedMarkets: filterClausesByPropertyKey.get('SelectedMarkets')?.selections,
          TradedMarkets: filterClausesByPropertyKey.get('TradedMarkets')?.selections,
          Sides: filterClausesByPropertyKey.get('Sides')?.selections,
          Symbols: filterClausesByPropertyKey.get('Symbols')?.selections,
          // Save the UI bound statuses in _ key. Save the real OrdStatuses used as Statuses
          _statuses: filterClausesByPropertyKey.get('_statuses')?.selections,
          Statuses: orderStatusFilterTextToBackendStatus(filterClausesByPropertyKey.get('_statuses')?.selections),
          Strategies: filterClausesByPropertyKey.get('Strategies')?.selections,
          SubAccounts: filterClausesByPropertyKey.get('SubAccounts')?.selections,
          Users: filterClausesByPropertyKey.get('Users')?.selections,
          Warnings: filterClausesByPropertyKey.get('Warnings')?.selections,
          ProductTypes: filterClausesByPropertyKey.get('ProductTypes')?.selections,
          PricingMode: filterClausesByPropertyKey.get('PricingMode')?.selections as PricingModeEnum[],
          UnifiedLiquidity: filterClausesByPropertyKey.get('UnifiedLiquidity')?.selections as
            | UnifiedLiquidityEnum[]
            | undefined,
          RequestSource: filterClausesByPropertyKey.get('RequestSource')?.selections as RequestSourceEnum[],
          // below are exact search and only one value is supported by backend
          OrderID: filterClausesByPropertyKey.get('OrderID')?.selections?.[0],
          RFQID: filterClausesByPropertyKey.get('RFQID')?.selections?.[0],
          ClOrdID: filterClausesByPropertyKey.get('ClOrdID')?.selections?.[0],
          ParentOrderID: filterClausesByPropertyKey.get('ParentOrderID')?.selections?.[0],
          ParentRFQID: filterClausesByPropertyKey.get('ParentRFQID')?.selections?.[0],
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
      permanentFilters,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });

  const { openClause } = filterBuilderAccordion;

  const getExtraMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => {
      return filterByColumnMainMenuItems({ params, colIDToFilterBuilderKey, openClause, mixpanel });
    },
    [mixpanel, openClause]
  );

  const { getContextMenuItems, dialogComponents } = useOrderMenu({
    openClause,
    filterableProperties,
    generateOrderDetailsRoute,
    layoutId,
  });

  const customerOrderByOrderIDObs = useRecentCustomerOrders(filteredOrders.filter);
  const enrichWithCustomerOrderFieldsPipe = useMemo(
    () =>
      pipe(
        combineLatestWith(customerOrderByOrderIDObs),
        map(([orders, customerOrders]) => {
          const enrichedData = orders.data.map(order => {
            if (order.ParentOrderID && customerOrders.has(order.ParentOrderID)) {
              const customerOrder = customerOrders.get(order.ParentOrderID);
              order.enrichOrderWithCustomerOrder(customerOrder);
            }
            return order;
          });
          return {
            ...orders,
            data: enrichedData,
          };
        })
      ),
    [customerOrderByOrderIDObs]
  ) satisfies CompositePipeFunction<Order>;

  const { showBlotterPauseButton } = useFeatureFlag();

  const serverFilter = useMemo(() => onlyServerFilterKeys(filteredOrders.filter), [filteredOrders.filter]);
  const { dataObservable, limitReachedState, raisePaginationLimits, errorObservable } = useFilteredOrdersBlotterObs({
    serverFilter,
  });

  const pipedSubscription = useObservable(
    () => dataObservable.pipe(enrichWithCustomerOrderFieldsPipe),
    [dataObservable, enrichWithCustomerOrderFieldsPipe]
  );

  const onOpenDetails = useNavigateToTradingOrderDetails();

  const blotterTable = useBlotterTable({
    dataObservable: pipedSubscription,
    errorObservable,
    rowID: 'OrderID',
    density: BlotterDensity.Compact,
    clientLocalFilter: clientSideFilter,
    columns,
    persistence: persisted,
    getExtraMainMenuItems,
    gridOptions: {
      // There are a few heavy valueGetters in this blotter. Enabling the value cache improves performance.
      valueCache: true,
      onSelectionChanged: handleRowSelectionChanged,
      onRowDoubleClicked(event) {
        const { data } = event;
        if (!data) {
          return;
        }
        if (onRowDoubleClicked) {
          onRowDoubleClicked(event);
        } else if (isEmbedded) {
          openDetailsDrawer({ entity: data });
        } else {
          onOpenDetails(data.OrderID);
        }
      },
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems,
      getRowStyle: getOrderRowStyle,
    },
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    exportOptions: {
      name: 'Orders',
      tabLabel,
    },
  });

  const dateRangeFilter = useDateRangeFilter(filteredOrders.filter, changeFilter, additionalFilterState?.DateRange);

  const handleExport = useCallback(
    (mode: ExportGridMode) => {
      const exportFunc = mode === 'CSV' ? blotterTable.exportDataAsCSV : blotterTable.exportDataAsExcel;
      exportFunc({
        fileName: createExportFileName({
          name: 'Orders',
          tabLabel,
          mode,
        }),
      });
    },
    [blotterTable.exportDataAsCSV, blotterTable.exportDataAsExcel, tabLabel]
  );

  const handleCloneTab = useCallback(() => {
    onCloneTab?.();
  }, [onCloneTab]);

  const exportDialog = useDisclosure();
  const handleExportServerData = useHandleExportOrders({ filter: filteredOrders.filter });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...dateRangeFilter}
        {...blotterTable.blotterTableFiltersProps}
        timestampFields={timestampFields}
        suffix={
          <OrderOptions
            changeFilter={changeFilter}
            filter={filteredOrders.filter}
            alwaysShowDDHOrders={alwaysShowDDHOrders}
            handleExport={() => {
              mixpanel.track(MixpanelEvent.ExportRowsModalOpen);
              exportDialog.open();
            }}
            handleCloneTab={onCloneTab ? handleCloneTab : undefined}
            tabType="FilteredOrders"
            blotterTable={blotterTable}
          />
        }
      />
      <MaxRecordsReachedWarning
        showLimitReached={!!limitReachedState?.lastRecord}
        earliestTimestamp={limitReachedState?.lastRecord?.Timestamp}
        recordsReceived={limitReachedState?.recordsReceived}
        onRaiseLimitClicked={raisePaginationLimits}
      />
      <BlotterTable {...blotterTable} />
      <SummaryLine
        homeCurrency={homeCurrency}
        rows={selectedOrders}
        getQuantity={getOrderQuantity}
        getAmount={getOrderAmount}
      />
      {dialogComponents}
      <ExportDialog
        {...exportDialog}
        handleExportLocalData={handleExport}
        handleExportServerData={handleExportServerData}
      />
    </>
  );
}

function onlyServerFilterKeys(filter: BlotterTableOrderFilter | undefined) {
  if (!filter) {
    return filter;
  }
  const serverFilter = pick(filter, [
    'TimestampField',
    'StartDate',
    'EndDate',
    'Statuses',
    'SubAccounts',
    'Symbols',
    'HideApiCalls',
    'OrderID',
    'RFQID',
    'ClOrdID',
    'ParentOrderID',
    'ParentRFQID',
  ]);
  if (filter._statuses) {
    serverFilter.Statuses = orderStatusFilterTextToBackendStatus(filter._statuses);
  }
  return serverFilter;
}

function getOrderRowStyle(params: RowClassParams<Order>) {
  if (params.data?.isArchived) {
    return { opacity: 0.5 };
  }
  return undefined;
}
